import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import {
  Payment,
  CreatePaymentRequest,
  CreatePaymentResponse,
} from "@/types/breeze/AnythinkPay";
import { anythinkPayService } from "@/services/breeze/AnythinkPayService";
import { PaginatedResult } from "@/types/breeze/Any";

// Query keys
export const anythinkPayPaymentKeys = {
  all: ["anythinkpay-payments"] as const,
  lists: () => [...anythinkPayPaymentKeys.all, "list"] as const,
  list: (page: number, pageSize: number, filters: string) =>
    [...anythinkPayPaymentKeys.lists(), { page, pageSize, filters }] as const,
  details: () => [...anythinkPayPaymentKeys.all, "detail"] as const,
  detail: (id: string) => [...anythinkPayPaymentKeys.details(), id] as const,
};

// Queries
export function useAnythinkPayPayments(
  page: number = 1,
  pageSize: number = 25,
  filter: string = "",
) {
  return useQuery<PaginatedResult<Payment>>({
    queryKey: anythinkPayPaymentKeys.list(page, pageSize, filter),
    queryFn: () => anythinkPayService.getPayments(page, pageSize, filter),
  });
}

export function useAnythinkPayPayment(id: string) {
  return useQuery<Payment>({
    queryKey: anythinkPayPaymentKeys.detail(id),
    queryFn: () => anythinkPayService.getPayment(id),
    enabled: !!id,
  });
}

export function useAnythinkPayPaymentsByStatus(
  status: string,
  page: number = 1,
  pageSize: number = 25,
  filter: string = "",
) {
  return useQuery<PaginatedResult<Payment>>({
    queryKey: anythinkPayPaymentKeys.list(
      page,
      pageSize,
      `status:${status}${filter ? `&${filter}` : ""}`,
    ),
    queryFn: () =>
      anythinkPayService.getPaymentsByStatus(status, page, pageSize, filter),
    enabled: status !== "all",
  });
}

// Mutations
export function useCreateAnythinkPayPayment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreatePaymentRequest) =>
      anythinkPayService.createPayment(data),
    onSuccess: (response: CreatePaymentResponse) => {
      // Invalidate and refetch payment lists
      queryClient.invalidateQueries({
        queryKey: anythinkPayPaymentKeys.lists(),
      });
      toast.success("Payment created successfully");
      return response;
    },
    onError: (error: any) => {
      toast.error(error?.message || "Failed to create payment");
    },
  });
}
